import { useMediaObservation } from "./SurveyObservationContext";
import { FeedbackMediaGallery } from "./FeedbackMediaGallery";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import type { CompiledSurvey } from "@apruv/survey-contract/compiler";
import type { ProofOfServiceMediaItem } from "../types";
import { useFlowRuntime } from "../FlowRuntimeContext";

type Configuration = CompiledSurvey["snapshot"]["configuration"];

const figmaMediaWidth = 393;
const figmaViewportHeight = 316;
const thumbnailRailContentHeight = 92;

export function orderedHeroMedia(
  items: ProofOfServiceMediaItem[],
  order: Configuration["hero"]["order"],
) {
  // API service records arrive in ascending source-record order. No capture
  // timestamps are exposed: oldest preserves that order; newest reverses it.
  const chronological = order === "newest" ? [...items].reverse() : [...items];
  const priority = (item: ProofOfServiceMediaItem) =>
    item.type === "video" ? 0 : item.type === "voice" ? 1 : 2;
  // Stable sort preserves configured chronological order within each media kind.
  return chronological.sort((a, b) => priority(a) - priority(b));
}

function previewUrl(item?: ProofOfServiceMediaItem) {
  if (!item) return undefined;
  return item.type === "image"
    ? (item.thumbnailUrl ?? item.url)
    : item.thumbnailUrl;
}

/** Canonical hero only. Gallery navigation remains manual and independent. */
export function FeedbackShellMedia({
  items,
  galleryItems = items,
  configuration,
  bottomInset,
  paused = false,
  onMinimumHeightChange,
  locale,
}: {
  items: ProofOfServiceMediaItem[];
  galleryItems?: ProofOfServiceMediaItem[];
  configuration: Configuration;
  locale?: string;
  bottomInset: number;
  paused?: boolean;
  onMinimumHeightChange?: (height: number) => void;
}) {
  const { hero, gallery, localization } = configuration;
  const local = (key: string, fallback: string) =>
    (locale && localization.supportedLocales.includes(locale)
      ? localization.translations[locale]?.[key]
      : undefined) ??
    localization.translations[localization.defaultLocale]?.[key] ??
    fallback;
  const text = (id: string, fallback: string) => {
    const key = `hero.${id}`;
    return local(
      `text/${encodeURIComponent(key)}/value`,
      configuration.content.text[key] ?? fallback,
    );
  };
  const mediaRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchInput = useRef(false);
  const positionId = useId();
  const controlsRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [controlsHeight, setControlsHeight] = useState(72);
  const [index, setIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string>();
  const [loadedKey, setLoadedKey] = useState<string>();
  const [videoMuted, setVideoMuted] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [obscured, setObscured] = useState(false);
  const [mediaWidth, setMediaWidth] = useState(figmaMediaWidth);
  const openGallery = () => {
    mediaRef.current
      ?.querySelectorAll<HTMLMediaElement>("video,audio")
      .forEach((player) => player.pause());
    setVideoMuted(true);
    setGalleryOpen(true);
  };
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { refreshMedia, refreshingMedia, mediaRevision = 0 } = useFlowRuntime();
  const previousRevision = useRef(mediaRevision);
  const ordered = orderedHeroMedia(items, hero.order);
  const orderedGallery = orderedHeroMedia(galleryItems, hero.order);
  const contents = JSON.stringify(ordered);
  const selected = Math.min(index, Math.max(0, ordered.length - 1));
  const item = ordered[selected];
  const url =
    item?.type === "image" ? (item.url ?? item.thumbnailUrl) : item?.url;
  const mediaKey = `${mediaRevision}:${contents}:${selected}:${url}`;
  const mediaObservation = useMediaObservation(
    item?.type === "note" ? undefined : item?.type,
    mediaKey,
  );
  // A repeated resource key is a new DOM visit. Clear readiness during commit,
  // before browser load events, including image → clip → same image returns.
  useLayoutEffect(() => setLoadedKey(undefined), [mediaKey]);
  const ready = loadedKey === mediaKey;
  const backdropUrl = previewUrl(item);
  const failed = !url || failedUrl === url;
  const serviceProof = hero.source === "service_proof";
  const hasThumbnailRail =
    serviceProof && hero.showProgress && ordered.length > 1;
  const thumbnailRailHeight = thumbnailRailContentHeight + bottomInset;
  const viewportHeight = hasThumbnailRail
    ? `calc(100% - ${thumbnailRailHeight}px)`
    : item?.type === "image"
      ? "100%"
      : `calc(100% - ${bottomInset + 1}px)`;

  useEffect(() => {
    const refreshed = previousRevision.current !== mediaRevision;
    previousRevision.current = mediaRevision;
    if (!refreshed) {
      setIndex(0);
    } else {
      setIndex((current) => Math.min(current, Math.max(0, ordered.length - 1)));
    }
    setVideoMuted(true);
    setFailedUrl(undefined);
  }, [contents, mediaRevision, ordered.length]);
  useEffect(() => {
    setFailedUrl(undefined);
    const active = document.activeElement;
    setFocused(
      Boolean(
        active &&
        mediaRef.current?.contains(active) &&
        !active.closest("[data-hero-controls]"),
      ),
    );
  }, [selected, url, contents, mediaRevision]);

  useLayoutEffect(() => {
    const element = mediaRef.current;
    if (!element) return;
    const measure = () => setMediaWidth(element.clientWidth || figmaMediaWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasThumbnailRail) return;
    thumbnailRefs.current[selected]?.scrollIntoView?.({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [hasThumbnailRail, reducedMotion, selected]);
  useEffect(() => {
    const update = () =>
      setObscured(
        document.hidden || Boolean(document.querySelector('[role="dialog"]')),
      );
    update();
    document.addEventListener("visibilitychange", update);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("visibilitychange", update);
      observer.disconnect();
    };
  }, []);
  const canAdvance =
    hero.autoplay && !reducedMotion && !paused && !obscured && !galleryOpen;
  const canPlayVideo =
    hero.autoplay && !reducedMotion && !paused && !obscured && !galleryOpen;
  const running = canAdvance && !hovered && !focused;
  useEffect(() => {
    // Images get a full interval after load. Clips own their duration and advance
    // only after playback ends.
    if (
      !running ||
      !ready ||
      failed ||
      item?.type !== "image" ||
      ordered.length < 2
    )
      return;
    const timer = window.setTimeout(() => {
      setVideoMuted(true);
      setIndex((current) => (current + 1) % ordered.length);
    }, hero.intervalMs);
    return () => window.clearTimeout(timer);
  }, [
    running,
    ready,
    failed,
    item?.type,
    selected,
    ordered.length,
    hero.intervalMs,
  ]);

  useEffect(() => {
    const player = videoRef.current;
    if (!player || item?.type !== "video") return;
    if (!canPlayVideo) {
      if (!player.paused) player.pause();
      return;
    }
    const playback = player.play();
    playback?.catch(() => {
      // Native controls remain available when a browser declines autoplay.
    });
  }, [canPlayVideo, item?.type, mediaKey]);

  useEffect(() => {
    const element = controlsRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const measure = () =>
      setControlsHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ordered.length > 0]);
  useEffect(() => {
    // Short configured aspect ratios still reserve usable native media and any
    // wrapped overlay controls. The shell adds the card overlap to this value.
    const nativeHeight = failed || item?.type !== "image" ? 200 : 80;
    const visibleViewport = Math.max(nativeHeight, controlsHeight + 32);
    const designedViewport = Math.max(
      nativeHeight,
      (mediaWidth * figmaViewportHeight) / figmaMediaWidth,
    );
    const requiredHeroHeight = hasThumbnailRail
      ? designedViewport + thumbnailRailHeight
      : visibleViewport + bottomInset + (item?.type === "image" ? 0 : 1);
    onMinimumHeightChange?.(Math.max(0, requiredHeroHeight - bottomInset));
  }, [
    bottomInset,
    controlsHeight,
    failed,
    hasThumbnailRail,
    item?.type,
    mediaWidth,
    thumbnailRailHeight,
    onMinimumHeightChange,
  ]);

  const choose = (next: number) => {
    setVideoMuted(true);
    setIndex(next);
  };
  const ended = () => {
    if (canAdvance && ordered.length > 1) {
      setVideoMuted(true);
      setIndex((current) => (current + 1) % ordered.length);
    }
  };
  const mediaStyle = {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: serviceProof ? "contain" : hero.crop,
    objectPosition: `${hero.focalPoint.x * 100}% ${hero.focalPoint.y * 100}%`,
    position: "relative",
    zIndex: 1,
  } as const;
  const fallback = text(
    "fallback",
    hero.fallback || "This media could not be loaded.",
  );
  const countBottom = item?.type === "video" ? 52 : bottomInset + 8;

  const renderThumbnail = (media: ProofOfServiceMediaItem) => {
    const thumbnailUrl = previewUrl(media);
    if (thumbnailUrl) {
      return (
        <Box
          alt=""
          component="img"
          src={thumbnailUrl}
          sx={{
            display: "block",
            height: "100%",
            objectFit: "cover",
            width: "100%",
          }}
        />
      );
    }
    if (media.type === "video" && media.url) {
      return (
        <Box
          aria-hidden="true"
          component="video"
          muted
          playsInline
          preload="metadata"
          src={media.url}
          tabIndex={-1}
          sx={{
            display: "block",
            height: "100%",
            objectFit: "cover",
            width: "100%",
          }}
        />
      );
    }
    const ThumbnailIcon =
      media.type === "video"
        ? MovieRoundedIcon
        : media.type === "voice"
          ? GraphicEqRoundedIcon
          : ImageRoundedIcon;
    return (
      <ThumbnailIcon sx={{ color: "rgba(255,255,255,0.72)", fontSize: 28 }} />
    );
  };

  const muteControl =
    item?.type === "video" ? (
      <IconButton
        aria-label={
          videoMuted
            ? text("unmuteLabel", "Unmute video")
            : text("muteLabel", "Mute video")
        }
        onClick={() => {
          const next = !videoMuted;
          setVideoMuted(next);
          if (videoRef.current) videoRef.current.muted = next;
        }}
        sx={{
          backdropFilter: "blur(1px)",
          bgcolor: "#22262F",
          borderRadius: "34px",
          color: "#fff",
          height: 26,
          p: "5px",
          width: 26,
          "&:hover": { bgcolor: "#303640" },
          "& svg": { fontSize: 16 },
        }}
      >
        {videoMuted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
      </IconButton>
    ) : null;
  const countControl = hero.showCount ? (
    <Stack
      component={gallery.enabled ? "button" : "div"}
      {...(gallery.enabled ? { type: "button" as const } : {})}
      onClick={gallery.enabled ? openGallery : undefined}
      aria-label={
        gallery.enabled ? text("galleryLabel", "Open media gallery") : undefined
      }
      direction="row"
      spacing="2px"
      aria-describedby={gallery.enabled ? positionId : undefined}
      data-testid="hero-count"
      sx={{
        alignItems: "center",
        backdropFilter: "blur(1px)",
        bgcolor: "#22262F",
        border: 0,
        borderRadius: "4px",
        color: "#fff",
        cursor: gallery.enabled ? "pointer" : "default",
        font: "inherit",
        height: 26,
        px: "6px",
        py: "2px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <Box
        component="img"
        src="/assets/survey/gallery-wide-bold.svg"
        alt=""
        sx={{ width: 16, height: 16 }}
      />
      <Typography id={positionId} sx={{ fontSize: 14, lineHeight: "22px" }}>
        {selected + 1}/{ordered.length}
      </Typography>
    </Stack>
  ) : null;

  return (
    <>
      <Box
        ref={mediaRef}
        data-testid="feedback-hero-media"
        onTouchStart={() => {
          touchInput.current = true;
          setHovered(false);
        }}
        onPointerMove={(event) => {
          if (event.pointerType === "mouse") touchInput.current = false;
        }}
        onMouseEnter={(event) =>
          !touchInput.current &&
          setHovered(!(event.target as Element).closest("[data-hero-controls]"))
        }
        onMouseMove={(event) =>
          !touchInput.current &&
          setHovered(!(event.target as Element).closest("[data-hero-controls]"))
        }
        onMouseLeave={() => setHovered(false)}
        onFocusCapture={(event) =>
          setFocused(!event.target.closest("[data-hero-controls]"))
        }
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setFocused(false);
        }}
        sx={{
          bgcolor: serviceProof ? "#0F1117" : configuration.tokens.surface,
          height: "100%",
          overflow: "hidden",
          position: "absolute",
          width: "100%",
        }}
      >
        {serviceProof && !failed && (backdropUrl || item?.type === "video") && (
          <Box
            aria-hidden="true"
            autoPlay={item?.type === "video" ? canPlayVideo : undefined}
            component={item?.type === "video" && !backdropUrl ? "video" : "img"}
            data-testid="hero-media-backdrop"
            muted={item?.type === "video" ? true : undefined}
            playsInline={item?.type === "video" ? true : undefined}
            preload={item?.type === "video" ? "metadata" : undefined}
            src={backdropUrl ?? url}
            tabIndex={item?.type === "video" ? -1 : undefined}
            sx={{
              filter: "blur(6px)",
              height: "calc(100% + 24px)",
              insetInlineStart: -12,
              objectFit: "cover",
              opacity: 0.7,
              position: "absolute",
              top: -12,
              width: "calc(100% + 24px)",
            }}
          />
        )}
        {serviceProof && !failed && (backdropUrl || item?.type === "video") && (
          <Box
            aria-hidden="true"
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.3)",
              inset: 0,
              position: "absolute",
            }}
          />
        )}

        <Box
          data-testid="hero-media-viewport"
          sx={{
            alignItems: "center",
            display: "flex",
            height: viewportHeight,
            insetInline: 0,
            justifyContent: "center",
            overflow: "hidden",
            position: "absolute",
            top: 0,
            zIndex: 1,
          }}
        >
          {failed ? (
            <Stack
              role="status"
              spacing={1}
              sx={{
                alignItems: "center",
                color: serviceProof ? "white" : configuration.tokens.text,
                height: "100%",
                justifyContent: "center",
                p: 2,
                position: "relative",
                width: "100%",
                zIndex: 1,
              }}
            >
              <Typography>{fallback}</Typography>
              {item && refreshMedia && (
                <Button
                  disabled={refreshingMedia}
                  sx={{ color: "inherit" }}
                  onClick={() => {
                    void refreshMedia().catch(() => setFailedUrl(url));
                  }}
                >
                  {refreshingMedia
                    ? text("refreshingLabel", "Refreshing media…")
                    : text("refreshLabel", "Refresh media")}
                </Button>
              )}
            </Stack>
          ) : item.type === "image" ? (
            <Box
              key={mediaKey}
              component="img"
              alt={item.label}
              src={url}
              onLoad={() => {
                setLoadedKey(mediaKey);
                mediaObservation.ready();
              }}
              onError={() => {
                setFailedUrl(url);
                mediaObservation.failed();
              }}
              sx={mediaStyle}
            />
          ) : item.type === "video" ? (
            <Box
              ref={videoRef}
              key={mediaKey}
              component="video"
              aria-label={item.label}
              autoPlay={canPlayVideo}
              controls
              controlsList="nodownload"
              disablePictureInPicture
              muted={videoMuted}
              playsInline
              preload="metadata"
              poster={item.thumbnailUrl}
              src={url}
              onLoadedMetadata={mediaObservation.ready}
              onEnded={ended}
              onError={() => {
                setFailedUrl(url);
                mediaObservation.failed();
              }}
              onVolumeChange={(event) =>
                setVideoMuted(event.currentTarget.muted)
              }
              sx={mediaStyle}
            />
          ) : (
            <Stack
              sx={{
                alignItems: "center",
                color: serviceProof ? "white" : configuration.tokens.text,
                height: "100%",
                justifyContent: "center",
                px: 2,
                position: "relative",
                width: "100%",
                zIndex: 1,
              }}
            >
              <Typography>{item.label}</Typography>
              <Box
                key={mediaKey}
                component="audio"
                aria-label={item.label}
                controls
                preload="metadata"
                src={url}
                onLoadedMetadata={mediaObservation.ready}
                onEnded={ended}
                onError={() => {
                  setFailedUrl(url);
                  mediaObservation.failed();
                }}
                sx={{ maxWidth: "100%" }}
              />
            </Stack>
          )}
          {hasThumbnailRail && (
            <Stack ref={controlsRef} data-hero-controls data-testid="hero-media-control-row" direction="row"
              sx={{ position: "absolute", top: 12, insetInline: 16, justifyContent: "space-between", zIndex: 3 }}>
              <Box>{muteControl}</Box>{countControl}
            </Stack>
          )}
          {hasThumbnailRail && (
            <Box data-hero-controls sx={{ position: "absolute", insetInline: 12, top: "50%", transform: "translateY(-50%)", display: "flex", justifyContent: "space-between", zIndex: 3, pointerEvents: "none" }}>
              {[-1, 1].map((delta) => (
                <IconButton key={delta} aria-label={delta < 0 ? "Previous media" : "Next media"}
                  onClick={() => choose((selected + delta + ordered.length) % ordered.length)}
                  sx={{ width: 44, height: 44, color: "white", bgcolor: "rgba(0,0,0,.55)", pointerEvents: "auto", '&:hover': { bgcolor: "rgba(0,0,0,.75)" } }}>
                  <span aria-hidden="true" style={{ fontSize: 32, lineHeight: 1 }}>{delta < 0 ? "‹" : "›"}</span>
                </IconButton>
              ))}
            </Box>
          )}
          {serviceProof && item?.type === "image" && item.label?.trim() && (
            <Box
              data-testid="hero-photo-title"
              aria-hidden="true"
              sx={{
                position: "absolute", insetInline: 0,
                bottom: hasThumbnailRail ? 0 : bottomInset,
                zIndex: 2, color: "#fff", fontSize: 14, fontWeight: 600,
                lineHeight: 1.4, pt: 3, pb: 1.5, px: 2,
                paddingInlineEnd: !hasThumbnailRail && hero.showCount ? "88px" : 2,
                background: "linear-gradient(transparent 0%, rgba(0,0,0,0.8) 43%, rgba(0,0,0,0.8) 100%)",
                overflowWrap: "anywhere", pointerEvents: "none",
              }}
            >
              {item.label}
            </Box>
          )}
          {!hasThumbnailRail && (muteControl || countControl) && (
            <Stack
              ref={controlsRef}
              data-hero-controls
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                bottom: countBottom,
                insetInline: 16,
                justifyContent: "space-between",
                position: "absolute",
                zIndex: 2,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {muteControl}
              </Stack>
              {countControl}
            </Stack>
          )}
        </Box>

        {hasThumbnailRail && (
          <Box
            data-hero-controls
            data-testid="hero-media-thumbnails"
            sx={{
              backdropFilter: "blur(15px)",
              borderTop: "1px solid rgba(145, 158, 171, 0.2)",
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              height: `${thumbnailRailHeight}px`,
              insetInline: 0,
              overflow: "hidden",
              pb: `${bottomInset}px`,
              px: 2,
              pt: 1,
              position: "absolute",
              zIndex: 1,
            }}
          >
            <Stack
              data-testid="hero-media-thumbnail-list"
              direction="row"
              spacing={1}
              sx={{
                flex: "0 0 70px",
                height: 70,
                minHeight: 70,
                overscrollBehaviorInline: "contain",
                overflowX: "auto",
                scrollBehavior: reducedMotion ? "auto" : "smooth",
                scrollbarWidth: "none",
                width: "100%",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {ordered.map((media, position) => (
                <ButtonBase
                  aria-current={position === selected ? "true" : undefined}
                  aria-label={text(
                    "selectLabel",
                    "Show media {number}",
                  ).replace("{number}", String(position + 1))}
                  key={`${media.type}:${media.label}:${position}`}
                  onClick={() => choose(position)}
                  ref={(element) => {
                    thumbnailRefs.current[position] = element;
                  }}
                  sx={{
                    alignItems: "center",
                    bgcolor: "#373737",
                    border: "2px solid",
                    borderColor:
                      position === selected ? "white" : "transparent",
                    borderRadius: "6px",
                    boxSizing: "border-box",
                    display: "flex",
                    flex: "0 0 calc((100% - 24px) / 4)",
                    height: "100%",
                    justifyContent: "center",
                    minHeight: 70,
                    minWidth: 0,
                    overflow: "hidden",
                    scrollSnapAlign: "nearest",
                    "&:focus-visible": {
                      outline: "2px solid white",
                      outlineOffset: -4,
                    },
                  }}
                >
                  {renderThumbnail(media)}
                </ButtonBase>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
      <FeedbackMediaGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        items={orderedGallery}
        initialIndex={Math.max(0, orderedGallery.indexOf(ordered[selected]))}
        configuration={configuration}
        locale={locale}
      />
    </>
  );
}
